import moment from "moment";
import { formatDisplayTime } from "../../helpers/timeDisplay";

const asId = (value) => (value === undefined || value === null ? "" : String(value));

const asDateTime = (value) => (value ? String(value) : "");

const CALENDAR_DATE_TIME_FORMATS = [
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DD HH:mm:ss",
];

const parseCalendarDateTime = (value) => {
  if (!value) return null;

  const text = String(value);
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(text);
  const parsed = hasOffset
    ? moment.parseZone(text)
    : moment(text, CALENDAR_DATE_TIME_FORMATS, true);

  return parsed.isValid() ? parsed : null;
};

/**
 * FullCalendar includes the salon offset in startStr/endStr. Keep that
 * wall-clock value when sending it to the legacy appointment APIs; using
 * new Date() here would silently convert it to the browser's timezone.
 */
export const formatCalendarWallClock = (value) => {
  const parsed = parseCalendarDateTime(value);
  if (!parsed) return null;

  return {
    date: parsed.format("YYYY-MM-DD"),
    dateTime: parsed.format("YYYY-MM-DD HH:mm:ss"),
    time: parsed.format("HH:mm:ss"),
    label: parsed.format("dddd, DD MMMM YYYY"),
  };
};

/** Format a salon-local calendar timestamp for the detail drawer. */
export const formatCalendarTimeLabel = (value) => {
  const wallClock = formatCalendarWallClock(value);
  if (!wallClock) return "";
  return formatDisplayTime(wallClock.dateTime);
};

/** Format a salon-local calendar timestamp as the drawer's date heading. */
export const formatCalendarDateLabel = (value) => {
  const wallClock = formatCalendarWallClock(value);
  if (!wallClock) return "";
  return moment(wallClock.dateTime, "YYYY-MM-DD HH:mm:ss", true).format("dddd, MMMM Do YYYY");
};

/**
 * Resolve FullCalendar EventApi strings without converting the salon wall
 * clock through the browser timezone. Named FullCalendar timezones can use
 * UTC-coerced Date objects when no timezone implementation is installed, so
 * ``event.start``/``event.end`` are not safe inputs for API payloads.
 */
export const getCalendarEventWallClockRange = (event = {}) => {
  const source = event && typeof event === "object" ? event : {};
  const start = formatCalendarWallClock(source.startStr || source.start);
  const end = formatCalendarWallClock(source.endStr || source.end);
  return {
    start: start?.dateTime || "",
    end: end?.dateTime || "",
    startDate: start?.date || "",
    endDate: end?.date || "",
  };
};

const formatAppointmentDetailTimestamp = (value) => {
  const wallClock = formatCalendarWallClock(value);
  return wallClock ? wallClock.dateTime : (value ? String(value) : "");
};

const formatAppointmentDetailDate = (value) => {
  const wallClock = formatCalendarWallClock(value);
  if (wallClock) return wallClock.date;

  if (!value) return "";
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "dddd, DD MMMM YYYY", "dddd, D MMMM YYYY"],
    true,
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const appointmentDetailLegacyTimestamp = (date, time, fallback) => {
  if (date && time) return `${date}T${time}`;
  return fallback || "";
};

/**
 * Resolve the timestamps rendered by the appointment detail drawer. The
 * show* values are salon-local display values; normalize offset-bearing
 * values to a wall-clock string before rendering so the browser timezone
 * cannot shift the appointment again.
 */
export const getAppointmentDetailDisplayRange = (detail = {}) => {
  const source = detail && typeof detail === "object" ? detail : {};
  const startCandidate = source.showstartdate || appointmentDetailLegacyTimestamp(
    source.dateof,
    source.start_time,
    source.start_datetime,
  );
  const endCandidate = source.showenddate || appointmentDetailLegacyTimestamp(
    source.dateof,
    source.end_time,
    source.end_datetime,
  );

  return {
    start: formatAppointmentDetailTimestamp(startCandidate),
    end: formatAppointmentDetailTimestamp(endCandidate),
    startDate: formatAppointmentDetailDate(startCandidate) || formatAppointmentDetailDate(source.dateof),
    endDate: formatAppointmentDetailDate(endCandidate) || formatAppointmentDetailDate(source.dateof),
  };
};

/**
 * Normalize FullCalendar's day/week selection into the values consumed by
 * the appointment and busy-time dialogs. Week selections do not carry a
 * resource object, so the header attributes are an explicit fallback.
 */
export const getCalendarSlotContext = ({
  startStr,
  endStr,
  allDay = false,
  viewType = "",
  resource,
  header = {},
} = {}) => {
  const resourceProps = resource?.extendedProps || {};
  let optionType = resourceProps.optionType || header.optionType || "";
  let staffId = resourceProps.staff_id ?? header.staff_id ?? "";
  let resourceItemId = resourceProps.resource_item_id ?? header.resource_item_id ?? "";
  const headerValue = header.selectedOptionType ?? header.selected_option_type ?? "";

  if (viewType === "timeGridWeek") {
    optionType = header.optionType || optionType;
    if (optionType === "staff") {
      staffId = headerValue === "all_staff" ? "" : (headerValue || staffId);
      resourceItemId = "";
    } else if (optionType === "resourceItem") {
      staffId = "";
      resourceItemId = headerValue === "all_resource_item" ? "" : (headerValue || resourceItemId);
    } else if (optionType === "resource") {
      staffId = "";
      resourceItemId = headerValue === "all_resource" ? "" : (headerValue || resourceItemId);
    }
  }

  const startWallClock = formatCalendarWallClock(startStr);
  const endWallClock = formatCalendarWallClock(endStr);

  return {
    resourceId: resource?.id || "",
    staff_id: asId(staffId),
    resource_item_id: asId(resourceItemId),
    optionType,
    allDay,
    dateof: startWallClock?.date || (startStr ? moment(startStr).format("YYYY-MM-DD") : ""),
    start_datetime: asDateTime(startStr),
    end_datetime: asDateTime(endStr),
    start_time: startWallClock?.time || "",
    end_time: endWallClock?.time || "",
  };
};

/** Values used to hydrate AppointmentAddForm after a selected slot. */
export const buildAppointmentFormValues = (data = {}) => {
  const start = data.start_datetime || data.startStr || "";
  const ending = data.ending || "";
  const startWallClock = formatCalendarWallClock(start);
  return {
    client_id: data.client_id || "",
    service_id: data.service_id || "",
    add_on_within_original_time: [true, 1, "1"].includes(data.add_on_within_original_time ?? data.service?.add_on_within_original_time),
    staff_id: data.staff_id || "",
    dateof: startWallClock?.label || (start ? moment(start).format("dddd, DD MMMM YYYY") : (data.dateof || "")),
    start_time: startWallClock?.time.slice(0, 5) || (start ? moment(start).format("HH:mm") : (data.start_time || "")),
    start_datetime: startWallClock?.dateTime || asDateTime(start),
    end_datetime: formatCalendarWallClock(data.end_datetime || data.endStr)?.dateTime || asDateTime(data.end_datetime || data.endStr),
    duration: data.duration || "",
    padding_time: data.padding_time || "00:00",
    durationWithPadding: data.durationWithPadding || "",
    cost: data.cost || "",
    booking_notes: data.booking_notes || "",
    repeats: data.repeats || "No",
    repeat_time: data.repeat_time || "",
    repeat_time_option: data.repeat_time_option || "",
    ending: ending ? moment(ending).format("DD MMMM YYYY") : "",
    resource_id: data.resource_id || "",
    resource_item_id: data.resource_item_id || "",
    resource_allocations: Array.isArray(data.resource_allocations) ? data.resource_allocations : [],
    totaldeposit: data.deposit ?? data.totaldeposit ?? 0,
  };
};

/** Values used to hydrate BusytimeAddForm from a selected calendar slot. */
export const buildBusytimeFormValues = (detail = {}) => {
  const start = detail.showstartdate || detail.start_datetime || detail.startStr || (
    detail.dateof && detail.start_time
      ? `${detail.dateof}T${detail.start_time}`
      : ""
  );
  const end = detail.showenddate || detail.end_datetime || detail.endStr || (
    detail.dateof && detail.end_time
      ? `${detail.dateof}T${detail.end_time}`
      : ""
  );
  const startWallClock = formatCalendarWallClock(start);
  const endWallClock = formatCalendarWallClock(end);
  return {
    id: detail.id || "",
    staff_id: detail.staff_id || "",
    dateof: startWallClock?.label || (start ? moment(start).format("dddd, DD MMMM YYYY") : (detail.dateof || "")),
    start_time: startWallClock?.time.slice(0, 5) || (start ? moment(start).format("HH:mm") : (detail.start_time || "")),
    end_time: endWallClock?.time.slice(0, 5) || (end ? moment(end).format("HH:mm") : (detail.end_time || "")),
    start_datetime: startWallClock?.dateTime || asDateTime(start),
    end_datetime: endWallClock?.dateTime || asDateTime(end),
    repeats: detail.repeats || "No",
    repeat_time: detail.repeat_time || "",
    repeat_time_option: detail.repeat_time_option || "",
    ending: detail.ending ? moment(detail.ending).format("DD MMMM YYYY") : "",
    reason: detail.reason || "",
    calendar_note: Boolean(detail.calendar_note ?? detail.is_calendar_note),
    repeat_busytime: detail.repeat_busytime || "",
  };
};

/** Null-safe resource lookup for week selections that have no resource. */
export const getCalendarEventResource = (eventInfo) => {
  const resourceId = eventInfo?.event?._def?.resourceIds?.[0];
  const calendar = eventInfo?.view?.calendar;
  if (!resourceId || typeof calendar?.getResourceById !== "function") return null;
  return calendar.getResourceById(resourceId) || null;
};

const printAppointmentFromEvent = (event) => {
  const props = event?.extendedProps || {};
  const appointment = props.appointment || {};
  const client = props.client || {};
  const eventWallClock = getCalendarEventWallClockRange(event);
  return {
    id: appointment.id || props.appointmentId || "",
    status: appointment.status || "",
    showstartdate: eventWallClock.start,
    showenddate: eventWallClock.end,
    dateof: eventWallClock.startDate,
    client: {
      id: client.id,
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      phone_number: client.phone_number || "",
    },
    staff: props.staff || {},
    service: props.service || {},
    resource_item: props.resource_item || {},
    cost: appointment.cost ?? props.cost ?? 0,
    addonservices: appointment.addonservices || props.addonservices || [],
    addanotherservices: appointment.addanotherservices || props.addanotherservices || [],
  };
};

/**
 * Build the same day-envelope shape as the legacy print endpoint from rows
 * already loaded for the calendar. This keeps print usable when Flask has no
 * legacy `/afterlogin/print/calendarprint` route yet.
 */
export const buildCalendarPrintDataFromEvents = (events = [], status = "all") => {
  const grouped = {};
  (Array.isArray(events) ? events : [])
    .filter((event) => event?.extendedProps?.listview === "Appointment")
    .filter((event) => event?.extendedProps?.view !== "paddingtime")
    .map(printAppointmentFromEvent)
    .filter((appointment) => status === "all" || !status || appointment.status === status)
    .forEach((appointment) => {
      const date = appointment.dateof || "unknown";
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(appointment);
    });

  return Object.keys(grouped).sort().map((dateof) => ({ dateof, data: grouped[dateof] }));
};

const splitDisplayName = (value) => {
  const parts = typeof value === "string" ? value.trim().split(/\s+/).filter(Boolean) : [];
  return {
    first_name: parts.shift() || "",
    last_name: parts.join(" "),
  };
};

const normalizePrintPerson = (person) => {
  if (!person || typeof person !== "object") return {};
  const splitName = splitDisplayName(person.name);
  return {
    ...person,
    first_name: person.first_name || splitName.first_name,
    last_name: person.last_name || splitName.last_name,
  };
};

const normalizePrintRelation = (relation) => {
  if (!relation || typeof relation !== "object") return {};
  return {
    ...relation,
    name: typeof relation.name === "string" ? relation.name : "",
  };
};

const normalizePrintResource = (resource) => {
  const normalized = normalizePrintRelation(resource);
  if (Object.keys(normalized).length === 0) return {};
  return {
    ...normalized,
    resource_item: normalized.resource_item || normalized.name || "",
  };
};

const asPrintTime = (value) => {
  if (!value) return "";
  const match = String(value).match(/T(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : String(value);
};

const normalizeFlaskChild = (child) => {
  if (!child || typeof child !== "object") return null;
  const start = child.start_time || child.start_datetime || "";
  const end = child.end_time || child.end_datetime || "";
  return {
    id: child.id ?? child.appointment_id ?? "",
    start_time: asPrintTime(start),
    end_time: asPrintTime(end),
    cost: child.cost ?? child.price ?? 0,
    staff: normalizePrintPerson(child.staff),
    service: normalizePrintRelation(child.service),
    resource_item: normalizePrintResource(child.resource),
  };
};

const normalizeFlaskAppointment = (appointment, dateof) => {
  if (!appointment || typeof appointment !== "object" || appointment.appointment_id === undefined) {
    return appointment;
  }
  const start = appointment.showstartdate || appointment.start_datetime || "";
  const end = appointment.showenddate || appointment.end_datetime || "";
  const children = Array.isArray(appointment.children)
    ? appointment.children.map(normalizeFlaskChild).filter(Boolean)
    : [];
  return {
    id: appointment.appointment_id,
    showstartdate: start,
    showenddate: end,
    dateof: appointment.dateof || dateof || String(start).slice(0, 10),
    status: appointment.status || "",
    cost: appointment.cost ?? appointment.price ?? 0,
    client: normalizePrintPerson(appointment.client),
    staff: normalizePrintPerson(appointment.staff),
    service: normalizePrintRelation(appointment.service),
    resource_item: normalizePrintResource(appointment.resource),
    addonservices: children.filter((child) => appointment.children.find((source) => (
      (source?.id ?? source?.appointment_id) === child.id && source?.kind === "addon"
    ))),
    addanotherservices: children.filter((child) => appointment.children.find((source) => (
      (source?.id ?? source?.appointment_id) === child.id && source?.kind === "add_another"
    ))),
  };
};

const normalizePrintDay = (day) => ({
  ...day,
  data: (Array.isArray(day?.data) ? day.data : []).map((appointment) => (
    normalizeFlaskAppointment(appointment, day?.dateof)
  )),
});

/** Accept both the legacy day-envelope and a plain appointment list. */
export const normalizeCalendarPrintRows = (payload) => {
  const flaskDays = Array.isArray(payload?.days)
    ? payload.days.map((day) => ({
      dateof: day?.date || day?.dateof || day?.showdate || "unknown",
      data: Array.isArray(day?.appointments)
        ? day.appointments.map((appointment) => normalizeFlaskAppointment(
          appointment,
          day?.date || day?.dateof || day?.showdate || "unknown"
        ))
        : (Array.isArray(day?.data) ? day.data : []),
    }))
    : [];
  const rows = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.data) ? payload.data : flaskDays);
  if (rows.length === 0) return [];
  if (rows.every((row) => Array.isArray(row?.data))) return rows.map(normalizePrintDay);

  const grouped = {};
  rows.forEach((row) => {
    const dateof = row?.dateof || row?.showdate || row?.showstartdate || row?.start_datetime || "unknown";
    if (!grouped[dateof]) grouped[dateof] = [];
    grouped[dateof].push(normalizeFlaskAppointment(row, dateof));
  });
  return Object.keys(grouped).sort().map((dateof) => ({
    dateof: String(dateof).slice(0, 10),
    data: grouped[dateof],
  }));
};

const CALENDAR_PRINT_FAILURE_MESSAGE = "Unable to print the calendar. Please try again.";

/** Extract the public message from either Axios or the Flask error envelope. */
export const getCalendarPrintFailureMessage = (action = {}) => {
  const payload = action?.payload;
  const response = payload?.message;
  const candidates = [
    response?.error?.message,
    response?.message,
    response?.error,
    payload?.error?.message,
    payload?.message,
    action?.error?.message,
  ];
  const message = candidates.find((candidate) => (
    typeof candidate === "string" && candidate.trim().length > 0
  ));
  return message || CALENDAR_PRINT_FAILURE_MESSAGE;
};

/**
 * Resolve the print modal outcome without treating a failed empty fallback as
 * a successful print. A successful API response is authoritative, including
 * a valid zero-row snapshot; local rows are only a compatibility fallback for
 * a failed request.
 */
export const resolveCalendarPrintResult = (action = {}, fallbackData = []) => {
  const fallbackRows = normalizeCalendarPrintRows(fallbackData);
  if (action?.meta?.requestStatus === "fulfilled") {
    return {
      kind: "open",
      data: normalizeCalendarPrintRows(action.payload),
    };
  }
  if (action?.meta?.requestStatus === "rejected" && fallbackRows.length > 0) {
    return { kind: "open", data: fallbackRows };
  }
  return {
    kind: "error",
    message: getCalendarPrintFailureMessage(action),
  };
};

export default {
  getCalendarSlotContext,
  formatCalendarWallClock,
  getCalendarEventWallClockRange,
  getAppointmentDetailDisplayRange,
  buildAppointmentFormValues,
  buildBusytimeFormValues,
  getCalendarEventResource,
  buildCalendarPrintDataFromEvents,
  normalizeCalendarPrintRows,
  getCalendarPrintFailureMessage,
  resolveCalendarPrintResult,
};
